// pages/TicketManagement/TicketManagementPage.jsx
//
// ┌─────────────────────────────────────────────────────────────────┐
// │  Mirrors SchemeManagementPage pattern exactly:                  │
// │  • useTableDataWithPost  for data fetching                      │
// │  • useSession            for userid                             │
// │  • useRolePermissions    for role-gated actions                 │
// │  • TicketFormModal       for create / edit                      │
// │  • TicketViewModal       for full detail + comments             │
// │  • DeleteModal           for delete confirmation                │
// └─────────────────────────────────────────────────────────────────┘

import React, { useState, useMemo } from "react";
import DataTable        from "components/DataTable";
import PageWrapperForm from "components/PageWrapperForm";
import DeleteModal from "components/DeleteModal";
import { useTableDataWithPost } from "hooks/useTableWithPost";
import { useSession } from "hooks/useSession";
import { useRolePermissions }   from "./components/useRolePermissions";
import { TicketManagement } from "./components/ticketManagement";
import TicketFormModal  from "./components/TicketFormModal";
import TicketViewModal  from "./components/TicketViewModal";


/* ─────────────────────────────────────────────
   Inline Stat Card  (self-contained, no deps)
───────────────────────────────────────────── */
const StatCard = ({ label, value, icon, color, bg }) => (
  <div style={{
    background: "#111318", border: "1px solid #262c3a", borderRadius: "12px",
    padding: "18px 22px", position: "relative", overflow: "hidden", flex: 1, minWidth: "140px",
  }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: color }} />
    <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "#525d72", marginBottom: "8px" }}>
      {label}
    </div>
    <div style={{ fontSize: "28px", fontWeight: 700, color: "#e8eaf0", fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
      {value ?? "—"}
    </div>
    <div style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", fontSize: "26px", opacity: .08 }}>
      {icon}
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   Priority / Status inline badges for table
───────────────────────────────────────────── */
const PBadge = ({ val }) => {
  const m = TicketManagement.badgeMap.priority[val] || { color: "#8892a4", bg: "#1e2330", label: val };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      background: m.bg, color: m.color, fontSize: "11px", fontWeight: 700,
      padding: "3px 9px", borderRadius: "20px", whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};
const SBadge = ({ val }) => {
  const m = TicketManagement.badgeMap.status[val] || { color: "#8892a4", bg: "#1e2330", label: val };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      background: m.bg, color: m.color, fontSize: "11px", fontWeight: 700,
      padding: "3px 9px", borderRadius: "20px", whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};

/* ═══════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════ */
const TicketManagementPage = () => {
  /* ── Session & roles ── */
  const { userid }             = useSession();
  const { can, roleName }      = useRolePermissions(TicketManagement);

  /* ── Modal state (same pattern as SchemeManagementPage) ── */
  const [modalShow, setModalShow]     = useState(false);
  const [modalMode, setModalMode]     = useState("add");
  const [viewShow,  setViewShow]      = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [refresh, setRefresh]         = useState(0); // 🔄 trigger re-fetch

  /* ── Table data fetch (same pattern) ── */
  const formdata = useMemo(() => {
    const fd = new FormData();
    fd.append("action",  "GET");
    fd.append("userid",  userid);
    fd.append("role",    roleName);
    return fd;
  }, [userid, roleName, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableData = useTableDataWithPost(
    TicketManagement.apiUrl,
    TicketManagement,
    formdata,
    refresh
  );

  /* ── Derive stats from tableData ── */
  const stats = useMemo(() => {
    const rows = tableData?.data || [];
    return {
      total:      rows.length,
      open:       rows.filter(r => r.status === "open").length,
      inProgress: rows.filter(r => r.status === "in_progress").length,
      critical:   rows.filter(r => r.priority === "critical" && ["open", "in_progress"].includes(r.status)).length,
      resolved:   rows.filter(r => r.status === "resolved").length,
    };
  }, [tableData]);

  /* ── Handlers (exactly mirroring SchemeManagementPage) ── */
  const handleAddTicket = () => {
    if (!can.create) return;
    setModalMode("add");
    setSelectedTicket(null);
    setModalShow(true);
  };

  const handleView = (ticket) => {
    setSelectedTicket(ticket);
    setViewShow(true);
  };

  const handleEditTicket = (ticket) => {
    if (!can.edit) return;
    setModalMode("edit");
    setSelectedTicket(ticket);
    setModalShow(true);
  };

  const handleDelete = (ticket) => {
    if (!can.delete) return;
    setSelectedTicket(ticket);
    setTimeout(() => {
      const modalEl = document.getElementById("deleteticket1");
      if (modalEl) {
        const modal = window.bootstrap?.Modal?.getOrCreateInstance(modalEl);
        modal?.show();
      }
    }, 100);
  };

  const handleExport = (selectedIds) => {
    if (!can.export) return;
    console.log("Exporting tickets:", selectedIds);
    alert(`Exporting ${selectedIds.length} ticket records`);
  };

  const handleDeleteSelected = (selectedIds) => {
    if (!can.delete) return;
    if (window.confirm(`Delete ${selectedIds.length} tickets?`)) {
      setRefresh(prev => prev + 1);
    }
  };

  /* ── Success callbacks (same pattern) ── */
  const handleFormSuccess = () => {
    console.log("TicketFormModal success — refreshing...");
    setRefresh(prev => prev + 1);
    setModalShow(false);
  };

  const handleDeleteSuccess = () => {
    console.log("Ticket deleted — refreshing...");
    setRefresh(prev => prev + 1);
  };

  const handleStatusChange = (newStatus) => {
    // Optimistically update the selected ticket's status
    setSelectedTicket(prev => prev ? { ...prev, status: newStatus } : prev);
    setRefresh(prev => prev + 1);
  };

  /* ── Open edit from view modal ── */
  const handleEditFromView = () => {
    setViewShow(false);
    setModalMode("edit");
    setModalShow(true);
  };

  return (
    <div style={{marginTop:"40px", padding:"20px"}}>
      {/* ── Stat Cards ── */}
      <div style={{
        display: "flex", gap: "12px", flexWrap: "wrap",
        marginBottom: "20px", fontFamily: "'DM Sans', sans-serif",
      }}>
        <StatCard label="Total Tickets" value={stats.total}      icon="🎫" color="#00d4aa" />
        <StatCard label="Open"          value={stats.open}       icon="📬" color="#3b82f6" />
        <StatCard label="In Progress"   value={stats.inProgress} icon="⚙️"  color="#f59e0b" />
        <StatCard label="Critical"      value={stats.critical}   icon="🔥" color="#ef4444" />
        <StatCard label="Resolved"      value={stats.resolved}   icon="✅" color="#10b981" />
      </div>

      {/* ── Role Notice ── */}
      {!can.create && (
        <div style={{
          background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.2)",
          borderRadius: "8px", padding: "10px 16px", marginBottom: "16px",
          fontSize: "12.5px", color: "#f59e0b", display: "flex", alignItems: "center", gap: "8px",
        }}>
          <span>ℹ️</span>
          Your role (<strong>{roleName.replace(/_/g, " ")}</strong>) can only view tickets.
          Contact your admin to raise a ticket.
        </div>
      )}

      {/* ── Main Table (same PageWrapperForm + DataTable pattern) ── */}
      <PageWrapperForm
        config={TicketManagement}
        handleOnclick={handleAddTicket}
        buttonTitle={can.create ? "🎫 New Ticket" : null}
      >
        <DataTable
          config={TicketManagement}
          tableData={tableData}
          onView={handleView}
          onEdit={can.edit   ? handleEditTicket   : null}
          onDelete={can.delete ? handleDelete     : null}
          onExport={can.export ? handleExport     : null}
          onDeleteSelected={can.delete ? handleDeleteSelected : null}

          /* Custom cell renderers for status / priority badges */
          cellRenderer={(col, value, row) => {
            if (col.key === "status")   return <SBadge val={value} />;
            if (col.key === "priority") return <PBadge val={value} />;
            if (col.key === "ticket_type") {
              const opt = TicketManagement.ticketTypeOptions.find(o => o.value === value);
              return <span style={{ fontSize: "12px", color: "#8892a4" }}>{opt?.label || value}</span>;
            }
            if (col.key === "amount") {
              return value
                ? <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: "#00d4aa" }}>
                    ₹ {Number(value).toLocaleString("en-IN")}
                  </span>
                : <span style={{ color: "#525d72", fontSize: "12px" }}>—</span>;
            }
            if (col.key === "assigned_to") {
              return row.assigned_to_name
                ? <span style={{ fontSize: "12.5px", color: "#e8eaf0" }}>{row.assigned_to_name}</span>
                : <span style={{ fontSize: "12px", color: "#525d72" }}>Unassigned</span>;
            }
            return value ?? "—";
          }}
        />
      </PageWrapperForm>

      {/* ── Create / Edit Modal ── */}
      <TicketFormModal
        show={modalShow}
        onClose={() => setModalShow(false)}
        mode={modalMode}
        userData={selectedTicket}
        onSuccess={handleFormSuccess}
      />

      {/* ── View Detail Modal ── */}
      <TicketViewModal
        show={viewShow}
        onClose={() => setViewShow(false)}
        ticket={selectedTicket}
        onStatusChange={handleStatusChange}
        onEditOpen={handleEditFromView}
      />

      {/* ── Delete Confirmation Modal (reusing your DeleteModal) ── */}
      <DeleteModal
        deleteid={selectedTicket}
        id="deleteticket1"
        title="Delete Ticket"
        message={`Are you sure you want to delete ticket ${selectedTicket?.ticket_id || ""}? This action cannot be undone.`}
        apiUrl={TicketManagement.apiUrl}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default TicketManagementPage;